import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import {
  audienceService,
  Audience,
  PaginatedAudiences,
  AudienceFilters,
} from '../services/audienceService';

interface AudienceState {
  audiences: Audience[];
  paginatedData: PaginatedAudiences | null;
  progressiveAudiences: (Audience | null)[];
  progressiveTotal: number;
  progressiveLoading: boolean;
  selectedAudience: Audience | null;
  loading: boolean;
  error: string | null;
  filters: AudienceFilters;
  currentPage: number;
  pageSize: number;
}

type AudienceAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_AUDIENCES'; payload: Audience[] }
  | { type: 'SET_PAGINATED_DATA'; payload: PaginatedAudiences }
  | { type: 'SET_SELECTED_AUDIENCE'; payload: Audience | null }
  | { type: 'SET_FILTERS'; payload: AudienceFilters }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SET_PAGE_SIZE'; payload: number }
  | { type: 'ADD_AUDIENCE'; payload: Audience }
  | { type: 'UPDATE_AUDIENCE'; payload: Audience }
  | { type: 'REMOVE_AUDIENCE'; payload: number }
  | { type: 'START_PROGRESSIVE_LOADING'; payload: { total: number; pageSize: number } }
  | { type: 'ADD_PROGRESSIVE_AUDIENCE'; payload: { audience: Audience; index: number } }
  | { type: 'END_PROGRESSIVE_LOADING' }
  | { type: 'RESET_PROGRESSIVE' }
  | { type: 'RESET' };

const initialState: AudienceState = {
  audiences: [],
  paginatedData: null,
  progressiveAudiences: [],
  progressiveTotal: 0,
  progressiveLoading: false,
  selectedAudience: null,
  loading: false,
  error: null,
  filters: {},
  currentPage: 1,
  pageSize: 20,
};

function audienceReducer(state: AudienceState, action: AudienceAction): AudienceState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_AUDIENCES':
      return { ...state, audiences: action.payload, loading: false, error: null };
    case 'SET_PAGINATED_DATA':
      return { ...state, paginatedData: action.payload, loading: false, error: null };
    case 'SET_SELECTED_AUDIENCE':
      return { ...state, selectedAudience: action.payload };
    case 'SET_FILTERS':
      return { ...state, filters: action.payload, currentPage: 1 };
    case 'SET_PAGE':
      return { ...state, currentPage: action.payload };
    case 'SET_PAGE_SIZE':
      return { ...state, pageSize: action.payload, currentPage: 1 };
    case 'ADD_AUDIENCE':
      return {
        ...state,
        audiences: [action.payload, ...state.audiences],
        paginatedData: state.paginatedData
          ? {
              ...state.paginatedData,
              total: state.paginatedData.total + 1,
              audiences:
                state.currentPage === 1
                  ? [action.payload, ...state.paginatedData.audiences.slice(0, state.pageSize - 1)]
                  : state.paginatedData.audiences,
            }
          : null,
      };
    case 'UPDATE_AUDIENCE':
      const updatedAudiences = state.audiences.map((a) =>
        a.id === action.payload.id ? action.payload : a,
      );
      return {
        ...state,
        audiences: updatedAudiences,
        selectedAudience:
          state.selectedAudience?.id === action.payload.id
            ? action.payload
            : state.selectedAudience,
        paginatedData: state.paginatedData
          ? {
              ...state.paginatedData,
              audiences: state.paginatedData.audiences.map((a) =>
                a.id === action.payload.id ? action.payload : a,
              ),
            }
          : null,
      };
    case 'REMOVE_AUDIENCE':
      return {
        ...state,
        audiences: state.audiences.filter((a) => a.id !== action.payload),
        selectedAudience:
          state.selectedAudience?.id === action.payload ? null : state.selectedAudience,
        paginatedData: state.paginatedData
          ? {
              ...state.paginatedData,
              total: state.paginatedData.total - 1,
              audiences: state.paginatedData.audiences.filter((a) => a.id !== action.payload),
            }
          : null,
      };
    case 'START_PROGRESSIVE_LOADING':
      return {
        ...state,
        progressiveLoading: true,
        progressiveAudiences: new Array(
          Math.min(action.payload.pageSize, action.payload.total),
        ).fill(null),
        progressiveTotal: action.payload.total,
        error: null,
      };
    case 'ADD_PROGRESSIVE_AUDIENCE':
      const newProgressiveAudiences = [...state.progressiveAudiences];
      newProgressiveAudiences[action.payload.index] = action.payload.audience;
      return {
        ...state,
        progressiveAudiences: newProgressiveAudiences,
      };
    case 'END_PROGRESSIVE_LOADING':
      return {
        ...state,
        progressiveLoading: false,
      };
    case 'RESET_PROGRESSIVE':
      return {
        ...state,
        progressiveAudiences: [],
        progressiveTotal: 0,
        progressiveLoading: false,
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

interface AudienceContextValue extends AudienceState {
  // Actions
  loadAllAudiences: () => Promise<void>;
  loadPaginatedAudiences: (
    page?: number,
    pageSize?: number,
    filters?: AudienceFilters,
  ) => Promise<void>;
  loadAudiencesProgressively: (
    page?: number,
    pageSize?: number,
    filters?: AudienceFilters,
  ) => Promise<void>;
  loadAudience: (audienceId: number) => Promise<Audience | null>;
  selectAudience: (audience: Audience | null) => void;
  setFilters: (filters: AudienceFilters) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  refreshAudiences: () => Promise<void>;
  clearError: () => void;
  prefetchAudiences: (audienceIds: number[]) => Promise<void>;
  resetAudienceData: () => void;

  // Cache management
  clearCache: () => void;
  getCacheStats: () => { size: number; keys: string[]; activeRequests: number };
}

const AudienceDataContext = createContext<AudienceContextValue | null>(null);

interface AudienceDataProviderProps {
  children: ReactNode;
  initialPageSize?: number;
}

export const AudienceDataProvider: React.FC<AudienceDataProviderProps> = ({
  children,
  initialPageSize = 20,
}) => {
  const [state, dispatch] = useReducer(audienceReducer, {
    ...initialState,
    pageSize: initialPageSize,
  });

  const loadAllAudiences = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const audiences = await audienceService.fetchAllAudiences();
      dispatch({ type: 'SET_AUDIENCES', payload: audiences });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load audiences';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      console.error('Failed to load all audiences:', error);
    }
  }, []);

  const loadPaginatedAudiences = useCallback(
    async (
      page: number = state.currentPage,
      pageSize: number = state.pageSize,
      filters: AudienceFilters = state.filters,
    ) => {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      try {
        const paginatedData = await audienceService.fetchAudiencesPaginated(
          page,
          pageSize,
          filters,
        );
        dispatch({ type: 'SET_PAGINATED_DATA', payload: paginatedData });
        dispatch({ type: 'SET_PAGE', payload: page });
        if (pageSize !== state.pageSize) {
          dispatch({ type: 'SET_PAGE_SIZE', payload: pageSize });
        }
        if (filters !== state.filters) {
          dispatch({ type: 'SET_FILTERS', payload: filters });
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load audiences';
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        console.error('Failed to load paginated audiences:', error);
      }
    },
    [state.currentPage, state.pageSize, state.filters],
  );

  const loadAudiencesProgressively = useCallback(
    async (
      page: number = state.currentPage,
      pageSize: number = state.pageSize,
      filters: AudienceFilters = state.filters,
    ) => {
      dispatch({ type: 'RESET_PROGRESSIVE' });
      dispatch({ type: 'SET_ERROR', payload: null });
      dispatch({ type: 'SET_PAGE', payload: page });

      if (pageSize !== state.pageSize) {
        dispatch({ type: 'SET_PAGE_SIZE', payload: pageSize });
      }
      if (filters !== state.filters) {
        dispatch({ type: 'SET_FILTERS', payload: filters });
      }

      try {
        // First, get the total count quickly
        const allIds = await audienceService.fetchAudienceIds();
        const startIndex = (page - 1) * pageSize;
        const actualPageSize = Math.min(pageSize, allIds.length - startIndex);

        // Initialize with the correct number of skeletons
        dispatch({
          type: 'START_PROGRESSIVE_LOADING',
          payload: {
            total: allIds.length,
            pageSize: actualPageSize > 0 ? actualPageSize : 0,
          },
        });

        if (actualPageSize <= 0) {
          dispatch({ type: 'END_PROGRESSIVE_LOADING' });
          return;
        }

        const generator = audienceService.fetchAudiencesProgressive(page, pageSize, filters);

        for await (const result of generator) {
          dispatch({
            type: 'ADD_PROGRESSIVE_AUDIENCE',
            payload: { audience: result.audience, index: result.index },
          });
        }

        dispatch({ type: 'END_PROGRESSIVE_LOADING' });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load audiences';
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        dispatch({ type: 'END_PROGRESSIVE_LOADING' });
        console.error('Failed to load audiences progressively:', error);
      }
    },
    [state.currentPage, state.pageSize, state.filters],
  );

  const loadAudience = useCallback(
    async (audienceId: number): Promise<Audience | null> => {
      try {
        const audience = await audienceService.fetchAudience(audienceId);

        // Update the audience in our state if it exists
        const existingIndex = state.audiences.findIndex((a) => a.id === audienceId);
        if (existingIndex >= 0) {
          dispatch({ type: 'UPDATE_AUDIENCE', payload: audience });
        }

        return audience;
      } catch (error) {
        console.error(`Failed to load audience ${audienceId}:`, error);
        return null;
      }
    },
    [state.audiences],
  );

  const selectAudience = useCallback((audience: Audience | null) => {
    dispatch({ type: 'SET_SELECTED_AUDIENCE', payload: audience });
  }, []);

  const setFilters = useCallback((filters: AudienceFilters) => {
    dispatch({ type: 'SET_FILTERS', payload: filters });
  }, []);

  const setPage = useCallback((page: number) => {
    dispatch({ type: 'SET_PAGE', payload: page });
  }, []);

  const setPageSize = useCallback((pageSize: number) => {
    dispatch({ type: 'SET_PAGE_SIZE', payload: pageSize });
  }, []);

  const refreshAudiences = useCallback(async () => {
    audienceService.clearCache();
    if (state.paginatedData) {
      await loadPaginatedAudiences();
    } else {
      await loadAllAudiences();
    }
  }, [loadPaginatedAudiences, loadAllAudiences, state.paginatedData]);

  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', payload: null });
  }, []);

  const prefetchAudiences = useCallback(async (audienceIds: number[]) => {
    try {
      await audienceService.prefetchAudiences(audienceIds);
    } catch (error) {
      console.warn('Failed to prefetch audiences:', error);
    }
  }, []);

  const clearCache = useCallback(() => {
    audienceService.clearCache();
  }, []);

  const getCacheStats = useCallback(() => {
    return audienceService.getCacheStats();
  }, []);

  const resetAudienceData = useCallback(() => {
    dispatch({ type: 'RESET' });
    clearCache();
  }, [clearCache]);

  // Auto-refresh on mount and periodic refresh
  useEffect(() => {
    const refreshInterval = setInterval(
      () => {
        audienceService.refreshCache();
      },
      5 * 60 * 1000,
    ); // 5 minutes

    return () => clearInterval(refreshInterval);
  }, []);

  const contextValue: AudienceContextValue = {
    ...state,
    loadAllAudiences,
    loadPaginatedAudiences,
    loadAudiencesProgressively,
    loadAudience,
    selectAudience,
    setFilters,
    setPage,
    setPageSize,
    refreshAudiences,
    clearError,
    prefetchAudiences,
    resetAudienceData,
    clearCache,
    getCacheStats,
  };

  return (
    <AudienceDataContext.Provider value={contextValue}>{children}</AudienceDataContext.Provider>
  );
};

export const useAudienceData = (): AudienceContextValue => {
  const context = useContext(AudienceDataContext);
  if (!context) {
    throw new Error('useAudienceData must be used within an AudienceDataProvider');
  }
  return context;
};

export const useAudienceLoader = () => {
  const { loadAllAudiences, loadPaginatedAudiences, loading, error } = useAudienceData();

  return {
    loadAllAudiences,
    loadPaginatedAudiences,
    loading,
    error,
  };
};

export const useAudienceSelection = () => {
  const { selectedAudience, selectAudience, loadAudience } = useAudienceData();

  return {
    selectedAudience,
    selectAudience,
    loadAudience,
  };
};

export const useAudiencePagination = () => {
  const { paginatedData, currentPage, pageSize, setPage, setPageSize, loadPaginatedAudiences } =
    useAudienceData();

  return {
    paginatedData,
    currentPage,
    pageSize,
    setPage,
    setPageSize,
    loadPaginatedAudiences,
  };
};
